import { useCallback, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { usePylon } from '@renderer/providers/PylonProvider'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { OPEN_FEEDBACK_PARAM } from '@shared/settings/settings-deeplink'

/** Prefixed so beta feedback is filterable out of the support queue it shares. */
const BETA_FEEDBACK_PREFILL = 'Beta feedback: '

/**
 * Pylon drops `showNewMessage` (opening on its home screen instead) until its chat app has
 * rendered, which lands ~200-300ms after the script loads and has no event of its own.
 */
const WIDGET_SETTLE_MS = 1000

export function trackOpenFeedback(source: string) {
  AmplitudeRenderer.trackEvent('Get Help', { action: 'Give Feedback', source })
}

/**
 * Opens a feedback thread in the support widget. Every "Give feedback"
 * affordance goes through here.
 *
 * Not the older feedback modal. That one POSTs to `backend.highlightai.com`, a
 * host that no longer resolves, so every submit failed with
 * `ERR_NAME_NOT_RESOLVED`. Pylon is mounted in each window that offers
 * feedback: overlay, chat, settings.
 */
export function useOpenFeedback(source: string) {
  const pylon = usePylon()

  return useCallback(() => {
    trackOpenFeedback(source)
    pylon.showNewMessage(BETA_FEEDBACK_PREFILL)
  }, [pylon, source])
}

/**
 * Settings-window side of `feedbackSettingsRoute`: opens feedback once the widget
 * has loaded and settled, then drops the param. A freshly opened window gets the
 * param before either, and an earlier show is undone when the widget boots closed.
 */
export function useOpenFeedbackFromRoute() {
  const { isWidgetLoaded, showNewMessage } = usePylon()
  const [searchParams, setSearchParams] = useSearchParams()
  const requested = searchParams.has(OPEN_FEEDBACK_PARAM)

  useEffect(() => {
    if (!requested || !isWidgetLoaded) {
      return
    }
    const timer = setTimeout(() => {
      showNewMessage(BETA_FEEDBACK_PREFILL)
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.delete(OPEN_FEEDBACK_PARAM)
          return next
        },
        { replace: true },
      )
    }, WIDGET_SETTLE_MS)
    return () => clearTimeout(timer)
  }, [requested, isWidgetLoaded, showNewMessage, setSearchParams])
}
